/**
 * Minimal shape for API / form document lines when resolving VAT for display.
 */
export interface InvoiceLineVatDisplayInput {
  taxRate?: number | string | null;
  TaxRate?: number | string | null;
  vat?: number | string | null;
  Vat?: number | string | null;
  taxPercentage?: number | string | null;
  TaxPercentage?: number | string | null;
  /** Per-line tax category from API (often nominal 0.15 / 15 while product category is denormalized). */
  taxCategory?: { taxRate?: number | string | null } | null;
  TaxCategory?: { taxRate?: number | string | null; TaxRate?: number | string | null } | null;
  price?: number | string | null;
  Price?: number | string | null;
  taxAmount?: number | string | null;
  TaxAmount?: number | string | null;
  completeProduct?: {
    taxCategory?: { taxRate?: number | string | null } | null;
    tax?: { taxRate?: number | string | null } | null;
  } | null;
  product?: {
    taxCategory?: { taxRate?: number | string | null } | null;
    tax?: { taxRate?: number | string | null } | null;
  } | null;
}

function toDisplayPercent(raw: number): number {
  if (!Number.isFinite(raw) || raw === 0) {
    return 0;
  }
  return raw <= 1 ? raw * 100 : raw;
}

/**
 * Nominal VAT from product / tax category (ZATCA catalog rate), not implied by rounded line net/tax.
 */
function catalogNominalPercent(
  line: InvoiceLineVatDisplayInput | null | undefined,
): number {
  const ordered = [
    line?.completeProduct?.taxCategory?.taxRate,
    line?.product?.taxCategory?.taxRate,
    line?.completeProduct?.tax?.taxRate,
    line?.product?.tax?.taxRate,
  ];
  for (const v of ordered) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) {
      return toDisplayPercent(n);
    }
  }
  return 0;
}

/** Nominal % on the line's own tax category (API), before falling back to product catalog. */
function lineTaxCategoryNominalPercent(
  line: InvoiceLineVatDisplayInput | null | undefined,
): number {
  const raw =
    line?.taxCategory?.taxRate ??
    line?.TaxCategory?.TaxRate ??
    line?.TaxCategory?.taxRate;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    return toDisplayPercent(n);
  }
  return 0;
}

/**
 * When the backend stores an **effective** VAT % on taxCategory / taxRate (~14.94) but the line
 * math matches that effective rate, snap to common statutory display rates (5 / 15) used in SA.
 * Does not alter values that are not close to the implied effective rate.
 */
function snapStatutoryVatIfMisstoredEffective(
  line: InvoiceLineVatDisplayInput | null | undefined,
  candidate: number,
): number {
  if (!Number.isFinite(candidate) || candidate <= 0) {
    return candidate;
  }
  const price = Number(line?.price ?? line?.Price ?? 0);
  const tax = Number(line?.taxAmount ?? line?.TaxAmount ?? 0);
  if (!(price > 0) || !(tax >= 0)) {
    return candidate;
  }
  const implied = (tax / price) * 100;
  if (!Number.isFinite(implied) || implied <= 0) {
    return candidate;
  }
  if (Math.abs(candidate - implied) > 0.4) {
    return candidate;
  }
  if (implied > 14.35 && implied < 15.65) {
    return 15;
  }
  if (implied > 4.55 && implied < 5.45) {
    return 5;
  }
  return candidate;
}

/** Nominal % declared on the document line (authoritative when present). */
function explicitLineNominalPercent(
  line: InvoiceLineVatDisplayInput | null | undefined,
): number {
  const raw = Number(
    line?.taxPercentage ??
      line?.TaxPercentage ??
      line?.vat ??
      line?.Vat ??
      0,
  );
  return toDisplayPercent(raw);
}

/** Legacy line fields that may hold decimal rate (0.15) or percent (15). */
function lineTaxRatePercentOnly(
  line: InvoiceLineVatDisplayInput | null | undefined,
): number {
  const raw = Number(line?.taxRate ?? line?.TaxRate ?? 0);
  return toDisplayPercent(raw);
}

/**
 * Returns VAT **percentage** (0–100+) for invoice line UI and `InvoiceCalculationService` input.
 * Order: (1) explicit line nominal; (2) line tax category; (3) product catalog; (4) line taxRate.
 * Then snap when a stored ~14.94% clearly matches effective line math so PDF/UI show **15%**.
 */
export function getNominalLineVatPercentForDisplay(
  line: InvoiceLineVatDisplayInput | null | undefined,
): number {
  const explicit = explicitLineNominalPercent(line);
  if (explicit > 0) {
    return snapStatutoryVatIfMisstoredEffective(line, explicit);
  }
  const fromLineCategory = lineTaxCategoryNominalPercent(line);
  if (fromLineCategory > 0) {
    return snapStatutoryVatIfMisstoredEffective(line, fromLineCategory);
  }
  const fromCatalog = catalogNominalPercent(line);
  if (fromCatalog > 0) {
    return snapStatutoryVatIfMisstoredEffective(line, fromCatalog);
  }
  const legacy = lineTaxRatePercentOnly(line);
  return snapStatutoryVatIfMisstoredEffective(line, legacy);
}

/**
 * VAT % label for PDF/print: whole values as `15` (not `15.00`); fractional rates keep up to 2 decimals.
 */
export function formatVatPercentLabelForExport(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) {
    return '0';
  }
  const r = Math.round(n * 100) / 100;
  if (Math.abs(r - Math.round(r)) < 1e-9) {
    return String(Math.round(r));
  }
  return r.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
